/**
 * Shared types for the DTAUploadModal component tree.
 */

import type { ParsedFormResult, UploadType, DtaCreationType } from "../../services/dtaApi"

/**
 * Which step is currently displayed in the multi-step upload modal.
 */
export type Step =
  | "type-select"
  | "file-upload"
  | "domain-selection"
  | "blank-form"
  | "creating"
  | "complete"

/**
 * Lifecycle status of a single DTA creation request during the batch creation step.
 */
export type CreationStatus = "pending" | "creating" | "created" | "failed"

/**
 * Tracks the creation status of a single form's DTA, including the resulting ID or error.
 */
export interface CreationProgress {
  status: CreationStatus
  dta_id?: string
  error?: string
}

/**
 * Props for the top-level DTAUploadModal component.
 */
export interface DTAUploadModalProps {
  studyId: string
  onClose: () => void
  /**
   * Called after all DTA creations finish so the parent can refresh its DTA list.
   */
  onComplete: () => void
  /**
   * Handles document-type uploads. The modal delegates to the parent because the parent owns the
   * async extraction job tracking.
   */
  onDocumentUpload: (file: File, vendorId: string, vendorName: string) => Promise<void>
}

interface UploadTypeOption {
  type: DtaCreationType
  label: string
  description: string
  acceptedFormats: string[]
  formatsDisplay: string
}

/**
 * Upload type choices presented in the first step of the modal.
 */
/**
 * Upload type constants matching the API enum.
 */
export const UPLOAD_TYPES = {
  EDC: "edc" as const,
  SDTM: "sdtm" as const,
  DTA: "dta" as const,
  BLANK: "blank" as const,
} as const

/**
 * Feature flag for the SDTM mapping-spec upload flow (parse an SDTM mapping report -> one DTA per
 * dataset). Off for now: while off, the SDTM upload option is not offered, so users cannot upload
 * SDTM xlsx files. Flip to `true` to re-enable. (Display of any existing SDTM DTAs is unaffected -
 * this only gates starting a new SDTM upload.) Module-private: consumed only by the
 * UPLOAD_TYPE_OPTIONS filter below.
 */
const SDTM_UPLOAD_ENABLED = false

const ALL_UPLOAD_TYPE_OPTIONS: UploadTypeOption[] = [
  {
    type: "edc",
    label: "EDC Specification",
    description:
      "Create multiple Data Transfer Agreements from an EDC specification file. Currently supporting Medidata Rave ALS and Veeva SDS. You'll review the detected forms and choose which ones to create.",
    acceptedFormats: [".xlsx", ".xls"],
    formatsDisplay: "Excel",
  },
  {
    type: "sdtm",
    label: "SDTM Mapping Specification",
    description:
      "Create multiple Data Transfer Agreements from an SDTM mapping specification. The system detects each subject-data SDTM domain and you choose which ones to create as Data Transfer Agreements.",
    acceptedFormats: [".xlsx", ".xls"],
    formatsDisplay: "Excel",
  },
  {
    type: "dta",
    label: "Third-Party Document",
    description:
      "Create a Data Transfer Agreement from a third-party source document (lab, CRO, imaging provider, etc). The system will use AI to extract transfer metadata and rules.",
    acceptedFormats: [".pdf", ".docx", ".xlsx"],
    formatsDisplay: "PDF, Word, Excel",
  },
  {
    type: "blank",
    label: "Empty Data Transfer Agreement",
    description:
      "Create an empty Data Transfer Agreement and fill in transfer metadata and rules manually. Use this when no source document is available.",
    acceptedFormats: [],
    formatsDisplay: "",
  },
]

/**
 * Upload types offered in the creation modal. The SDTM option is excluded while
 * {@link SDTM_UPLOAD_ENABLED} is off, so the SDTM upload flow is unreachable.
 */
export const UPLOAD_TYPE_OPTIONS: UploadTypeOption[] = ALL_UPLOAD_TYPE_OPTIONS.filter(
  (o) => o.type !== "sdtm" || SDTM_UPLOAD_ENABLED,
)

export type { ParsedFormResult, UploadType, DtaCreationType }

/**
 * SDTM trial-design domain codes (study-design metadata, not typical vendor data transfers).
 * Datasets in these domains are shown in domain selection but left unselected by default. Mirrors
 * TRIAL_DESIGN_DOMAINS in the backend parser.
 */
export const TRIAL_DESIGN_DOMAINS = new Set(["TA", "TE", "TV", "TI", "TS", "TD", "TM"])
